<script>
import Vue from 'vue'
import CompanyResource from '@/resources/company'
import LookingForResource from '@/resources/lookingfor'
import MarketResource from '@/resources/market'
import MetroAreaResource from '@/resources/metroarea'
import SkillResource from '@/resources/skill'
import KeywordResource from '@/resources/keyword'
import RoleResource from '@/resources/role'
import UserResource from '@/resources/user'
import ListingResource from '@/resources/listing'
import ResourceSearch from '@/components/ResourceSearch.vue'
import JobPosting from '@/components/JobPosting.vue'
import RegionSelect from '../components/RegionSelect.vue'
import { quillEditor } from 'vue-quill-editor'

export default Vue.extend({
    computed: {
        selectedSkillIds() {
            return this.selectedSkillResources.map((resource) => resource.id)
        },
        selectedRoleName() {
            let selectedRole = this.roleOptions.find((item) => item.value === this.form.role)
            if (selectedRole) {
                return selectedRole.text
            }

            return 'Developer'
        },
        editorOptions() {
            return {
                placeholder: '',
                rows: '50',
                modules: {
                    toolbar: [['bold', 'italic', 'underline', 'strike'], [{ list: 'ordered' }, { list: 'bullet' }], [{ indent: '-1' }, { indent: '+1' }], ['clean']],
                },
            }
        },
    },
    components: {
        quillEditor,
        ResourceSearch,
        JobPosting,
        RegionSelect
    },
    data() {
        return {
            loading: false,
            formState: {},
            marketOptions: [],
            metroAreaResources: [],
            metroAreasSorted: {},
            roleOptions: [],
            lookingForOptions: [],
            selectedSkillResources: [],
            suggestedRoleResources: [],
            selectedRegions: [],
            regionalCandidatesOnly: true,
            viewingPreviewModal: false,
            remoteChoices: [
                {
                    value: 0,
                    text: 'No Preference',
                },
                {
                    value: 1,
                    text: 'Never',
                },
                {
                    value: 2,
                    text: 'Sometimes',
                },
                {
                    value: 3,
                    text: 'Most Days',
                },
                {
                    value: 4,
                    text: 'Remote Only',
                },
            ],
            form: {
                title: null,
                role: '6f90c38e-8c60-4fb4-918f-74fc0c3e0c74',
                type: 'fulltime',
                body: null,
                location: '',
                regions: [], // MetroArea
                regions_auto_skip: false,
                compensation_min: '',
                compensation_max: '',
                compensation_auto_skip: false,
                equity_percentage_min: '',
                equity_percentage_max: '',
                equity_auto_skip: false,
                apply_link_external: '',
                remote_level: 0, // readonly.remote_choices
                skills_required: [], // Skill
                skills: [],
                years_experience: 1,
                years_experience_auto_skip: false,
                keywords: [], //
                markets: [],
                is_draft: true,
            },
        }
    },
    props: {
        userResource: {
            type: UserResource,
            required: true,
        },
        companyResource: {
            type: CompanyResource,
            required: true,
        },
        listingResource: {
            type: ListingResource,
            required: false,
            default() {
                return new ListingResource({
                    company: this.companyResource.id,
                })
            },
        },
        showSubmit: {
            type: Boolean,
            default: true,
        },
        showCompany: {
            type: Boolean,
            default: false,
        },
        submitText: {
            type: String,
            default: 'Submit',
        },
        previewText: {
            type: String,
            default: 'Preview',
        },
        noPreview: {
            type: Boolean,
            default: false
        },
        maxSelectedRequiredSkills: {
            type: [Number, String],
            default: 5,
        },
    },
    methods: {
        onSubmit(options = { previewing: false }) {
            this.$emit('submit', this.listingResource)

            this.form.skills = this.selectedSkillResources.map((skillResource) => skillResource.id)
            this.form.regions = this.selectedRegions.map((regionResource) => regionResource.id)

            if (this.formState.$invalid) {
                return false
            }

            for (let key in this.form) {
                this.listingResource.attributes[key] = this.form[key]
            }

            if(options.previewing) {
                this.viewingPreviewModal = true
                return false
            }

            this.$emit('success', this.listingResource)
        },
        /**
         * Group all form events related to this.form and re-emit them as input of this component
         */
        onInput() {
            this.$emit('input', this.listingResource)
        },
        resourceToSelectOptions(resource) {
            return { value: resource.id, text: String(resource) }
        },
        selectSkill(skillResource) {
            this.selectedSkillResources.push(skillResource)
            this.updateSuggestedSkills()
        },
        async updateSuggestedSkills() {
            let result = await SkillResource.filter({ roles: this.form.role })
            this.suggestedRoleResources = result.resources.filter((item) => !~this.selectedSkillIds.indexOf(item.id)).slice(0, 5)
        },
        onRequiredSkillInput(skillResource, val) {
            if (val) {
                this.form['skills_required'].push(skillResource.id)
            } else {
                let skillIndex = this.form['skills_required'].indexOf(skillResource.id)
                this.form['skills_required'].splice(skillIndex, 1)
            }
        },
        metroRegionToKey(region) {
            return region.replace(/[\W_]+/g, '')
        },
        async hydrateForm(resource) {
            for (let key in this.form) {
                this.form[key] = resource.attributes[key]
            }

            let skillsResourceManager = resource.rel('skills')
            let regionResourceManager = resource.rel('regions')

            if (!skillsResourceManager.resolved) {
                await skillsResourceManager.resolve()
            }

            if(!regionResourceManager.resolved) {
                await regionResourceManager.resolve()
            }

            skillsResourceManager.resources.forEach((skillResource) => this.selectSkill(skillResource))
            this.selectedRegions = regionResourceManager.resources
        },
    },
    beforeMount() {
        this.$emit('input', this.listingResource)
    },
    async mounted() {
        this.loading = true

        let [marketResources, roleResources, lookingForResources] = await Promise.all([MarketResource.all(), RoleResource.all(), LookingForResource.all()])

        this.marketOptions = marketResources.map(this.resourceToSelectOptions)
        this.roleOptions = roleResources.map(this.resourceToSelectOptions)
        this.lookingForOptions = lookingForResources.map(this.resourceToSelectOptions)
        await this.updateSuggestedSkills()

        if ('object' === typeof this.listingResource && !this.listingResource.isNew()) {
            await this.hydrateForm(this.listingResource)
        }

        this.loading = false
    },
})
</script>

<template>
    <div>
        <vue-form :state="formState" @submit.prevent="onSubmit()" v-if="!loading">
            <b-form-group label="Company" v-if="showCompany">
                <b-form-input disabled class="col-md-6" :value="companyResource.attributes['name']" />
            </b-form-group>

            <validate auto-label>
                <b-form-group label="Job Title">
                    <b-form-input name="title" class="col-md-6" v-model="form.title" required @input="onInput" />
                    <field-messages name="title" show="$submitted">
                        <div slot="required" class="text-danger">This field is required</div>
                    </field-messages>
                </b-form-group>
            </validate>

            <validate auto-label>
                <b-form-group label="Employment Type">
                    <b-form-select name="role" class="col-md-6" v-model="form.type" required @input="onInput" :options="lookingForOptions" />
                    <field-messages name="role" show="$submitted">
                        <div slot="required" class="text-danger">This field is required</div>
                    </field-messages>
                </b-form-group>
            </validate>

            <validate auto-label>
                <b-form-group label="Role">
                    <b-form-select name="role" class="col-md-6" v-model="form.role" required @input="onInput" @change="updateSuggestedSkills()" :options="roleOptions" />
                    <field-messages name="role" show="$submitted">
                        <div slot="required" class="text-danger">This field is required</div>
                    </field-messages>
                </b-form-group>
            </validate>

            <b-form-group label="Job Description">
                <small class="form-text text-muted mb-2">Describe the job as clear as possible.</small>
                <quill-editor name="body" v-model="form.body" :options="editorOptions" @input="onInput" />
            </b-form-group>

            <h4 class="mt-5">Compensation</h4>
            <hr class="mb-3" />

            <b-row>
                <b-col md="6">
                    <b-row>
                        <b-col md="6">
                            <b-form-group label="Salary Min">
                                <b-input-group prepend="$">
                                    <b-form-input name="compensationMin" v-model="form['compensation_min']" @input="onInput" />
                                </b-input-group>
                            </b-form-group>
                        </b-col>
                        <b-col md="6">
                            <b-form-group label="Max">
                                <b-input-group prepend="$">
                                    <b-form-input name="compensationMax" v-model="form['compensation_max']" @input="onInput" />
                                </b-input-group>
                            </b-form-group>
                        </b-col>
                    </b-row>
                    <b-row>
                        <b-col md="6">
                            <b-form-group label="Equity Min">
                                <b-input-group append="%">
                                    <b-form-input name="equityMin" v-model="form['equity_percentage_min']" @input="onInput" />
                                </b-input-group>
                            </b-form-group>
                        </b-col>
                        <b-col md="6">
                            <b-form-group label="Max">
                                <b-input-group append="%">
                                    <b-form-input name="equityMax" v-model="form['equity_percentage_max']" @input="onInput" />
                                </b-input-group>
                            </b-form-group>
                        </b-col>
                    </b-row>
                </b-col>
            </b-row>

            <b-form-group>
                <b-form-checkbox
                    class="mb-2 mr-sm-2 mb-sm-0"
                    v-model="form['compensation_auto_skip']"
                    @input="
                        (val) => {
                            form['compensation_auto_skip'] = form['equity_auto_skip'] = Boolean(val)
                            onInput(val)
                        }
                    "
                    >Auto-skip candidates not in these ranges?</b-form-checkbox
                >
            </b-form-group>

            <h4 class="mt-5">Candidate Skillset</h4>
            <hr class="mb-2" />

            <b-form-group v-if="form.type !== 'internships'">
                <template slot="label">
                    Years Experience required as a
                    <strong>{{ selectedRoleName }}</strong>
                </template>
                <b-form-spinbutton v-model="form['years_experience']" min="1" max="15" step="1" class="col-md-3"></b-form-spinbutton>
            </b-form-group>

            <b-form-group v-if="form.type !== 'internships'">
                <b-form-checkbox
                    class="mb-2 mr-sm-2 mb-sm-0"
                    v-model="form['years_experience_auto_skip']"
                    @input="
                        (val) => {
                            form['years_experience_auto_skip'] = Boolean(val)
                            onInput(val)
                        }
                    "
                    >Auto-skip candidates with less than {{ form['years_experience'] == 1 ? `${form['years_experience']} year` : `${form['years_experience']} years` }} experience?</b-form-checkbox
                >
            </b-form-group>

            <b-row class="mt-5">
                <b-col md="6">
                    <resource-search
                        :resourceClass="$resources.SkillResource"
                        label="Select Skills Relevant to this Position"
                        name="skills"
                        height="250"
                        placeholder="Start typing to search... (eg. TypeScript)"
                        :filter="(resource) => !~selectedSkillIds.indexOf(resource.id)"
                        @input="onInput"
                        @select="selectSkill"
                    ></resource-search>
                </b-col>

                <b-col md="6">
                    <b-badge
                        href="javascript:{}"
                        variant="light"
                        v-if="selectedSkillResources.length > 0"
                        @click="
                            selectedSkillResources = []
                            form['skills_required'] = []
                        "
                        class="float-right"
                        >Reset</b-badge
                    >
                    <div class="pt-3" v-if="suggestedRoleResources.length > 0">
                        <small class="form-text text-muted">
                            Suggested Skills for
                            <strong>{{ selectedRoleName }}</strong
                            >:
                        </small>
                        <b-badge
                            v-for="(suggestedRoleResource, i) in suggestedRoleResources"
                            :key="i"
                            variant="primary"
                            href="javascript: {};"
                            @click="selectSkill(suggestedRoleResource)"
                            class="mr-1 mb-1"
                            >{{ suggestedRoleResource }}</b-badge
                        >
                    </div>
                </b-col>
            </b-row>

            <b-form-group v-if="selectedSkillResources.length > 0">
                <b-list-group>
                    <b-list-group-item v-for="(skillResource, i) in selectedSkillResources" :key="i" class="selected-skill-item">
                        <b-row>
                            <b-col md="8">
                                <strong>{{ skillResource }}</strong>
                            </b-col>
                            <b-col md="4" class="text-right">
                                <b-form-checkbox
                                    switch
                                    @input="(val) => onRequiredSkillInput(skillResource, val)"
                                    v-if="skillResource.get('priority') > 9 && form.type !== 'internships'"
                                    :disabled="!~form['skills_required'].indexOf(skillResource.id) && form['skills_required'].length >= maxSelectedRequiredSkills"
                                >
                                    <small class="text-muted float-right mt-1">Required</small>
                                </b-form-checkbox>
                            </b-col>
                        </b-row>
                    </b-list-group-item>
                </b-list-group>
            </b-form-group>

            <h4 class="mt-5">Candidate Location</h4>
            <hr class="mb-2" />

            <b-form-group>
                <b-form-checkbox class="mb-2 mr-sm-2 mb-sm-0" @input="onInput" v-model="regionalCandidatesOnly">Only candidates in a specific region will be considered</b-form-checkbox>
            </b-form-group>

            <div v-if="regionalCandidatesOnly">

                <region-select v-model="selectedRegions" />
                
                <b-form-group v-if="selectedRegions.length > 0">
                    <template slot="label">
                        Position open to candidates in:
                        <b-badge href="javascript:{}" variant="light" @click="selectedRegions = []" class="float-right">Reset</b-badge>
                    </template>
                    <b-badge v-for="(region, i) in selectedRegions" :key="i" class="mb-1 mr-1">{{ region }}</b-badge>
                </b-form-group>

                <b-form-group>
                    <b-form-checkbox
                        class="mb-2 mr-sm-2 mb-sm-0"
                        v-model="form['regions_auto_skip']"
                        @input="
                            (val) => {
                                form['regions_auto_skip'] = Boolean(val)
                                onInput(val)
                            }
                        "
                        >Auto-skip candidates not in these regions?</b-form-checkbox
                    >
                </b-form-group>

                <b-form-group label="Job Location" description="Specific location of where this job is located, if applicable.">
                    <b-form-input name="title" class="col-md-6" v-model="form.location" @input="onInput" placeholder="Eg. Potrero Hill, SF" />
                </b-form-group>

            </div>

            <b-form-group label="Remote / Work From Home Policy">
                <b-form-select :options="remoteChoices" v-model="form['remote_level']" class="col-md-6" @input="onInput" />
            </b-form-group>

            <div v-if="companyResource.get('tier') > 1">
                <h4 class="mt-5">Applicants/ATS</h4>
                <hr class="mb-2" />

                <validate auto-label>
                    <b-form-group label="External Apply Link" description="Instead of sending application to inbox, send applicant to this link when clicking Apply.">
                        <b-form-input name="applyLinkExternal" type="url" class="col-md-6" v-model="form.apply_link_external" @input="onInput" />
                        <field-messages name="applyLinkExternal" show="$submitted">
                            <div slot="url" class="text-danger">A valid URL is required (https://...)</div>
                        </field-messages>
                    </b-form-group>
                </validate>
                
                <b-form-group label="ATS Integration" description="ATS integration is currently disabled. Please check back soon.">
                    <b-form-checkbox disabled> Workable</b-form-checkbox>
                    <b-form-checkbox disabled> Lever</b-form-checkbox>
                    <b-form-checkbox disabled> Greenhouse</b-form-checkbox>
                </b-form-group>
            </div>

            <hr />

            <slot name="submit" v-if="showSubmit">
                <b-button type="submit" variant="primary" size="lg">{{ submitText }}</b-button>
                <b-button v-if="!noPreview" variant="link" size="lg" @click="onSubmit({ previewing: true })" :disabled="!formState.$valid">{{ previewText }}</b-button>
            </slot>
        </vue-form>

        <div v-else>
            Loading...
        </div>

        <b-modal v-model="viewingPreviewModal" title="Preview" size="xl" ok-title="Close" header-bg-variant="secondary" header-text-variant="light" ok-only centered no-fade>
            <job-posting :listing-resource="listingResource" />
        </b-modal>
    </div>
</template>

<style lang="scss">
.ql-editor {
    min-height: 250px;
    font-size: 16px;
}

.location-list {
    overflow-y: scroll;
    max-height: 315px;
}
</style>
